import React from 'react'
import { LucideIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { formatNumber } from '@/lib/format'
import CurrencyAmount from '@/components/shared/CurrencyAmount'
import { cn } from '@/lib/utils'

interface FinanceStatsCardProps {
  title: string
  value: number | string
  icon: LucideIcon
  format?: 'currency' | 'number'
  currency?: string
  subtitle?: string
  badge?: {
    text: string
    variant?: 'default' | 'secondary' | 'destructive' | 'outline'
  }
  trend?: 'up' | 'down' | 'neutral'
  className?: string
}

/**
 * Enhanced stats card specifically designed for financial metrics
 * Includes icons, badges, and different color themes
 */
export const FinanceStatsCard: React.FC<FinanceStatsCardProps> = ({
  title,
  value,
  icon: Icon,
  format = 'number',
  subtitle,
  badge,
  className,
}) => {
  // Format the main value
  const formattedValue = React.useMemo(() => {
    if (typeof value === 'string') return value
    switch (format) {
      case 'currency':
        return null // handled via CurrencyAmount component below
      case 'number':
      default:
        return formatNumber(value, { compact: value >= 1000 })
    }
  }, [value, format])

  return (
    <div className={cn('group relative bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 hover:border-primary/30 dark:hover:border-primary/50 transition-all duration-300 hover:shadow-lg hover:shadow-primary/10', className)}>
      {/* Animated accent bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-secondary to-primary rounded-t-2xl"></div>
      
      <div className="p-6">
        {/* Header with icon and title */}
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h3 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-widest mb-2">
              {title}
            </h3>
            <div className="flex items-baseline space-x-1">
              {format === 'currency' && typeof value === 'number' ? (
                <CurrencyAmount amount={value} textClassName="text-4xl font-extrabold text-gray-900 dark:text-white" />
              ) : (
                <span className="text-4xl font-extrabold text-gray-900 dark:text-white">
                  {formattedValue}
                </span>
              )}
            </div>
          </div>
          
          {/* Floating icon */}
          <div className="relative">
            <div className="w-14 h-14 bg-gradient-to-tr from-primary/10 to-secondary/10 rounded-2xl flex items-center justify-center border border-primary/20 group-hover:scale-110 transition-transform duration-300">
              <Icon className="h-7 w-7 text-primary" />
            </div>
            {/* Animated background glow */}
            <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-secondary/20 rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"></div>
          </div>
        </div>
        
        {/* Additional content */}
        <div className="space-y-3">
          {subtitle && (
            <div className="flex items-center space-x-2">
              <div className="w-1 h-4 bg-gradient-to-b from-primary to-secondary rounded-full"></div>
              <p className="text-sm text-gray-600 dark:text-gray-300 font-medium">
                {subtitle}
              </p>
            </div>
          )}
          
          {badge && (
            <div className="inline-flex">
              <Badge variant={badge.variant || 'secondary'} className="text-xs font-semibold px-3 py-1 bg-primary/10 text-primary border-primary/20">
                {badge.text}
              </Badge>
            </div>
          )}
        </div>
      </div>
    </div>
  )
} 